import isNil from 'lodash/isNil';
import { environment } from '@utility/app.util';
import { CustomEncoder } from '@utility/custom-encoder';
import { BehaviorSubject, Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatDialogRef } from '@angular/material/dialog';

import { ConfirmationModalComponent } from '@app/shared/confirmation-modal/confirmation-modal.component';

import { ConfirmationModalService } from '@app/shared/confirmation-modal/confirmation-modal.service';

import {
  UsersResponseDto,
  AuthorsResponseDto,
  DefaultResponseDto,
  PostsListResponseDto,
  DeletePostsResponseDto,
  SharePostResponseDto,
  ContentHubPreviewPost,
  PostDetailsResponseDto,
} from '@sociuu/interfaces';
import { IResendPostToNewUserPayload, IResendPostsPayload } from '@lib/posts.interface';
import { SendoutAction } from '@sociuu/lib/enum/post-status-name.enum';

import { resendEmailsConfirmationConfig } from '@app/shared/confirmation-modal/util';

export interface IPostsFilters {
  published: number;
  archived: number;
  sort_order: string;
  sort_by: string;
  key: string;
  networks: number[];
  authors: number[];
  date_from?: string;
  date_to?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PostsService {
  page = 1;
  pagination = 10;
  sort_by = 'created_at';
  sort_order = 'desc';
  filters = {
    published: 1,
    archived: 0,
    sort_order: this.sort_order,
    sort_by: this.sort_by,
    key: '',
    networks: [],
    authors: [],
    dateFrom: '',
    dateTo: '',
  };
  filterSubject: BehaviorSubject<IPostsFilters> = new BehaviorSubject<IPostsFilters>({ ...this.filters });
  currentFilters: Observable<IPostsFilters> = this.filterSubject.asObservable();

  constructor(
    private http: HttpClient,
    private _confirmationModalService: ConfirmationModalService,
  ) {}

  resetFilters(): void {
    this.filterSubject.next({ ...this.filters });
  }

  getPosts(page: number, filters: any, campaignType: string, includeChildPosts: boolean = false, exportType: 'csv' | 'xlsx' = null): Observable<PostsListResponseDto> {
    let str = '';
    if (filters.date_from && filters.date_from !== '-1') str += 'date_from=' + encodeURIComponent(filters.date_from) + '&';
    if (filters.date_to && filters.date_to !== '-1') str += 'date_to=' + encodeURIComponent(filters.date_to) + '&';
    this.filterSubject.next(filters);
    for (const p in filters) {
      if (filters.hasOwnProperty(p) && !['date_from', 'date_to', 'dateFrom', 'dateTo', 'interval'].includes(p) && filters[p]) {
        str += p + '=' + encodeURIComponent(filters[p]) + '&';
      }
    }
    if (exportType) {
      return this.http.get<PostsListResponseDto>(
        `${environment.apiUrl}/posts/list?file_type=${exportType}&campaign_type=${campaignType}&include_child_in_posts_list=${includeChildPosts}&${str}`
      );
    } else {
      return this.http.get<PostsListResponseDto>(
        `${environment.apiUrl}/posts/list?page=${page}&pagination=${this.pagination}&campaign_type=${campaignType}&include_child_in_posts_list=${includeChildPosts}&${str}`
      );
    }
  }

  getChildPosts(postId: number, campaignType: string): Observable<PostsListResponseDto> {
    let url: string = `/posts/list`;
    const isPublished: boolean = !!this.filterSubject.value['published'];
    return this.http.get<PostsListResponseDto>(
      `${environment.apiUrl}${url}?&include_child_in_posts_list=${true}&ids=${postId}&campaign_type=${campaignType}&published=${isPublished ? 1 : 0}&archived=${isPublished ? 0 : 1}`
    );
  }

  getPostsWebhook(page: number, filters: any, campaignType: string, includeChildPosts: boolean = false,  exportType: 'csv' | 'xlsx' = null): Observable<PostsListResponseDto> {
    const excludedKeys: string[] = ['date_from', 'date_to', 'dateFrom', 'dateTo', 'interval'];
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() })
      .set('campaign_type', String(campaignType))
      .set('include_child_in_posts_list', String(includeChildPosts));

    if (!isNil(filters?.date_from) && filters.date_from !== '' && filters.date_from !== '-1') {
      params = params.set('date_from', String(filters.date_from));
    }

    if (!isNil(filters?.date_to) && filters.date_to !== '' && filters.date_to !== '-1') {
      params = params.set('date_to', String(filters.date_to));
    }

    for (const [key, value] of Object.entries(filters || {})) {
      if (excludedKeys.includes(key)) continue;

      const hasValue: boolean = Array.isArray(value)
        ? value.length > 0
        : !isNil(value) && value !== '';

      if (!hasValue) continue;

      params = params.set(key, Array.isArray(value) ? value.join(',') : String(value));
    }

    if (exportType) {
      params = params.set('file_type', exportType);
      return this.http.get<PostsListResponseDto>(`${environment.apiUrl}/posts/list/webhooks`, { params });
    } else {
      params = params
        .set('page', String(page))
        .set('pagination', String(this.pagination));
      return this.http.get<PostsListResponseDto>(`${environment.apiUrl}/posts/list/webhooks`, { params });
    }
  }

  getPostDetails(post_id: number): Observable<PostDetailsResponseDto> {
    return this.http.get<PostDetailsResponseDto>(`${environment.apiUrl}/posts/details/${post_id}`);
  }

  getPostDetailsWebhook(post_id: number): Observable<PostDetailsResponseDto> {
    return this.http.get<PostDetailsResponseDto>(`${environment.apiUrl}/posts/details/webhooks/${post_id}`);
  }

  deletePost(post_id: number): Observable<DefaultResponseDto> {
    return this.http.delete<DefaultResponseDto>(`${environment.apiUrl}/posts/delete/${post_id}`);
  }

  deleteMultiplePosts(ids: number[]): Observable<DeletePostsResponseDto> {
    const data = { ids: ids };
    return this.http.post<DeletePostsResponseDto>(`${environment.apiUrl}/posts/delete-multiple`, data);
  }

  public resendPostsToNonOpeners(post_id: number, sendNow?: boolean): Observable<DefaultResponseDto> {
    const payload: IResendPostsPayload = {
      id: post_id,
      ...(!isNil(sendNow) && { send_now: sendNow }),
    };
    return this.http.post<DefaultResponseDto>(`${environment.apiUrl}/posts/emails/non-openers`, payload);
  }

  /**
   * Pauses, resumes or cancels a pulsing post's send-out. Cancel takes `cancel_non_opener_resend` when the post has a
   * resend to non-openers scheduled. A refused action (finished send-out, resume under the handbrake) answers 409.
   */
  public sendoutAction(post_id: number, action: SendoutAction, body: { cancel_non_opener_resend?: boolean } = {}): Observable<DefaultResponseDto> {
    return this.http.post<DefaultResponseDto>(`${environment.apiUrl}/posts/${post_id}/sendout/${action}`, body);
  }

  getAuthors(): Observable<AuthorsResponseDto> {
    return this.http.get<AuthorsResponseDto>(`${environment.apiUrl}/users/get-authors?pagination=99`);
  }

  public archivePosts(action: number, ids: number[]): Observable<any> {
    const status: string = action === 2 ? 'archive' : 'unarchive';
    return this.http.put(`${environment.apiUrl}/v2/posts/${status}`, { ids });
  }

  exportStats(
    date_from: string,
    date_to: string,
    interval = '365d',
    campaign_type: string,
    post_type = 'regular',
    export_type: 'csv' | 'xlsx' = 'xlsx',
    filters?: any,
  ): Observable<DefaultResponseDto> {
    const data = {
      interval,
      post_type,
      ...filters,
      campaign_type,
      fileType: export_type,
      ...( date_to && date_to !== '-1') && { date_to },
      ...( date_from && date_from !== '-1') && { date_from },
    };
    return this.http.post<DefaultResponseDto>(`${environment.apiUrl}/statistics/export`, data);
  }

  exportPostList(
    date_from: string,
    date_to: string,
    interval = '365d',
    campaign_type: string,
    post_type = 'regular',
    export_type: 'csv' | 'xlsx' = 'xlsx',
    filters?: any,
  ): Observable<DefaultResponseDto> {
    const data = {
      interval,
      post_type,
      ...filters,
      campaign_type,
      fileType: export_type,
      ...( date_to && date_to !== '-1') && { date_to },
      ...( date_from && date_from !== '-1') && { date_from },
    };
    return this.http.post<DefaultResponseDto>(`${environment.apiUrl}/posts/export`, data);
  }

  uploadImageToS3(link): Observable<any> {
    const postedData = { link: link };
    return this.http.post(environment.apiUrl + '/posts/uploadUrlImage', postedData);
  }

  getContentHubPosts(ids: string): Observable<{
    data: ContentHubPreviewPost[];
    deleted: ContentHubPreviewPost[];
  }> {
    let params = new HttpParams();
    params = params.append('ids', ids);

    return this.http.get<{
      data: ContentHubPreviewPost[];
      deleted: ContentHubPreviewPost[];
    }>(`${environment.apiUrl}/content-source/content-by-ids`, { params });
  }

  getContentHubPostsAndContents(post_ids: string, content_ids: string): Observable<any> {
    let params = new HttpParams();
    params = params.append('post_ids', post_ids);
    params = params.append('content_ids', content_ids);

    return this.http.get<any>(`${environment.apiUrl}/v01/posts/duplicate`, {
      params,
    });
  }

  resendPostToNewUsers(data: IResendPostToNewUserPayload): Observable<DefaultResponseDto> {
    return this.http.post<DefaultResponseDto>(`${environment.apiUrl}/v01/posts/update-users`, data);
  }

  getAllPostUsers(post_id: number): Observable<UsersResponseDto> {
    return this.http.get<UsersResponseDto>(
      `${environment.apiUrl}/v01/users/list/byPost/${post_id}?page=${this.page}&pagination=${9999}&include_stats=false`
    );
  }

  sharePost(event: string, shared_source, token): Observable<SharePostResponseDto> {
    const data = {
      event: event,
      token: token,
      shared_source: shared_source,
    };

    return this.http.post<SharePostResponseDto>(`${environment.apiUrl}/post/flag/create`, data);
  }

  getPostPreview(token): Observable<any> {
    return this.http.get(`${environment.apiUrl}/posts/getPreview?token=${token}`);
  }

  getScreenShot(token): Observable<any> {
    const params = new HttpParams().set('token', token);
    return this.http.get(`${environment.baseUrl}/public/screenshot`, { params });
  }

  getPostDetailsSociuuhub(post_id: number, campaign_type: string): Observable<PostDetailsResponseDto> {
    return this.http.get<PostDetailsResponseDto>(`${environment.apiUrl}/posts/details/pull/${post_id}?campaign_type=${campaign_type}`);
  }

  sendPostPreview(data): Observable<DefaultResponseDto> {
    return this.http.post<DefaultResponseDto>(`${environment.apiUrl}/posts/preview-new`, data);
  }

  public resendEmailToNonOpenersConfirmationDialog(): Promise<any> {
    let dialogRef: MatDialogRef<ConfirmationModalComponent, any>;
    dialogRef = this._confirmationModalService.open(resendEmailsConfirmationConfig());
    return new Promise((res, rej) => {
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'confirmed') res(true);
        else if (result === 'cancelled') res(false);
      });
    });
  }
}
